/**
 * @file        MyTeam.jsx
 * @module      modules/kyb/manager/console
 * @description "My team" on the Team and SLA page, for KYB managers: list
 *              their KYB executives, add one (a password is e-mailed), send a
 *              new password, deactivate / activate. Executives work cases in
 *              this console but can't approve, reject or override.
 */

import React, { useCallback, useEffect, useState } from "react";
import { kybManagerApi } from "../api/kybManagerApi";
import { Modal, RowActions, fmtWhen, initials, useToast } from "./ui";

function AddMemberModal({ onClose, onAdded }) {
  const toast = useToast();
  const [f, setF] = useState({ full_name: "", email: "", country_code: "91", phone_number: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: k === "phone_number" ? e.target.value.replace(/\D/g, "") : e.target.value }));

  const submit = async () => {
    if (f.full_name.trim().length < 2) return setErr("Enter the full name.");
    if (!/^\S+@\S+\.\S+$/.test(f.email.trim())) return setErr("Enter a valid e-mail address.");
    if (!/^\d{7,12}$/.test(f.phone_number)) return setErr("Enter a valid mobile number (digits only).");
    setBusy(true);
    setErr("");
    try {
      const res = await kybManagerApi.addTeamMember({ full_name: f.full_name.trim(), email: f.email.trim(), phone_number: f.phone_number, country_code: Number(f.country_code) || 91 });
      toast(res.emailed ? `${res.user.name} added. The password was e-mailed to ${res.user.email}` : `${res.user.name} added, but the e-mail couldn't be sent. Use “Send new password”`);
      onAdded();
    } catch (e) {
      setErr(e.message || "Couldn't add the team member.");
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  return (
    <Modal
      title="Add team member"
      sub="They sign in with this e-mail and the password we send them. Team members work cases; only you can approve, reject or override."
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={submit} disabled={busy}>
            Add and e-mail password
          </button>
        </>
      }
    >
      <div className="fld">
        <label htmlFor="tmName">Full name</label>
        <input id="tmName" type="text" autoFocus value={f.full_name} onChange={set("full_name")} maxLength={50} />
      </div>
      <div className="fld">
        <label htmlFor="tmEmail">E-mail</label>
        <input id="tmEmail" type="text" value={f.email} onChange={set("email")} maxLength={255} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "90px 1fr", gap: 10 }}>
        <div className="fld">
          <label htmlFor="tmCode">Code</label>
          <input id="tmCode" type="text" value={f.country_code} onChange={set("country_code")} maxLength={3} />
        </div>
        <div className="fld">
          <label htmlFor="tmMobile">Mobile</label>
          <input id="tmMobile" type="text" inputMode="numeric" value={f.phone_number} onChange={set("phone_number")} maxLength={12} />
        </div>
      </div>
      {err ? (
        <p className="err" role="alert">
          {err}
        </p>
      ) : null}
    </Modal>
  );
}

export default function MyTeam({ onChanged }) {
  const toast = useToast();
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    kybManagerApi
      .myTeam()
      .then((d) => {
        setItems(d.items);
        setError(null);
      })
      .catch((e) => setError(e.message || "Couldn't load your team."));
  }, []);
  useEffect(load, [load]);

  const act = async (u, fn, ok) => {
    setBusy(u.id);
    try {
      await fn();
      toast(ok);
      load();
      onChanged && onChanged();
    } catch (e) {
      toast(e.message || "Something went wrong.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <section style={{ marginTop: 28 }}>
      <div className="pagehead" style={{ marginBottom: 14 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22 }}>My team</h2>
          <p style={{ fontSize: 14 }}>People you added. They work cases in this console; approving, rejecting and overriding stay with you.</p>
        </div>
        <button className="btn primary" onClick={() => setAdding(true)}>
          Add team member
        </button>
      </div>
      {error ? <div className="empty">{error}</div> : null}
      {!items && !error ? <div className="loading">Loading…</div> : null}
      {items ? (
        <div className="tablewrap">
          {items.length ? (
            <table className="q" style={{ minWidth: 760 }}>
              <thead>
                <tr>
                  <th>Team member</th>
                  <th>Mobile</th>
                  <th>Status</th>
                  <th>Added</th>
                  <th style={{ width: 90 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((u) => (
                  <tr key={u.id} style={{ cursor: "default" }}>
                    <td>
                      <span style={{ display: "inline-flex", gap: 10, alignItems: "center" }}>
                        <span className="avatar grey">{initials(u.name)}</span>
                        <span className="biz">
                          <b>{u.name}</b>
                          <small>{u.email}</small>
                        </span>
                      </span>
                    </td>
                    <td>{u.mobile ? `${u.countryCode ? `+${u.countryCode} ` : ""}${u.mobile}` : "—"}</td>
                    <td>{u.active ? <span className="chip auto">Active</span> : <span className="chip progress">Deactivated</span>}</td>
                    <td style={{ color: "var(--muted)" }}>{fmtWhen(u.createdAt)}</td>
                    <td>
                      <RowActions
                        label={`Actions for ${u.name}`}
                        busy={busy === u.id}
                        items={[
                          {
                            key: "password",
                            label: "Send new password",
                            disabled: !u.active,
                            onClick: () => act(u, () => kybManagerApi.resendTeamMemberPassword(u.id), `A new password was e-mailed to ${u.email}`),
                          },
                          u.active
                            ? {
                                key: "deactivate",
                                label: "Deactivate",
                                danger: true,
                                onClick: () => act(u, () => kybManagerApi.setTeamMemberActive(u.id, false), `${u.name} can no longer sign in`),
                              }
                            : {
                                key: "activate",
                                label: "Activate",
                                onClick: () => act(u, () => kybManagerApi.setTeamMemberActive(u.id, true), `${u.name} can sign in again`),
                              },
                        ]}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="empty">
              <b style={{ color: "var(--text)" }}>No team members yet</b>
              <br />
              Add the people who review business verifications with you.
            </div>
          )}
        </div>
      ) : null}
      {adding ? (
        <AddMemberModal
          onClose={() => setAdding(false)}
          onAdded={() => {
            setAdding(false);
            load();
            onChanged && onChanged();
          }}
        />
      ) : null}
    </section>
  );
}
